import React, { useEffect, useState } from "react";
import { Box, Divider, Grid, MenuItem, Typography } from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiFormSelect from "src/components/core-components/mui/ByzMuiFormSelect";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import { referenceService } from "src/api/references/referenceService";
import { useAppSelector } from "src/app/hooks";
import { RootState } from "src/app/store";
import { WillOpenCardKey } from "src/api/request/requestService";
import { useTranslate } from "src/locales";

interface HeightInfoModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: (data: { height: string; muscleFactor: string }) => void;
  // Merkezi additional-info fetch için opsiyonel parametreler
  additionalInfoKey?: WillOpenCardKey;
  additionalInfoParams?: {
    serviceId: number;
    requestDetailId: number;
    ptId?: number;
    officeId?: number;
  };
}

const HeightInfoModal: React.FC<HeightInfoModalProps> = ({
  open,
  onClose,
  onSave,
  additionalInfoKey,
  additionalInfoParams,
}) => {
  const { t } = useTranslate();
  const { selectedOfficeId } = useAppSelector((state: RootState) => ({
    selectedOfficeId: state.auth.selectedOfficeId,
  }));

  const [additionalInfo, setAdditionalInfo] = useState<any>(null);
  const [height, setHeight] = useState("");
  const [muscleFactor, setMuscleFactor] = useState("");

  useEffect(() => {
    if (!open || !additionalInfoKey) return;

    const loadAdditionalInfo = async () => {
      try {
        const res = await referenceService.additionalInfo.getFields({
          formtype: additionalInfoKey,
          offid:
            additionalInfoParams?.officeId ??
            (selectedOfficeId ? Number(selectedOfficeId) : undefined),
        });
        // TODO: Dönen alanlara göre kas faktörü seçenekleri/varsayılanları ayarlanabilir.
        if (res) {
          setAdditionalInfo(res);
        }
      } catch {
        // Hata durumunda mevcut manuel akış devam eder.
      }
    };

    loadAdditionalInfo();
  }, [open, additionalInfoKey, additionalInfoParams, selectedOfficeId]);

  const handleSave = () => {
    onSave?.({ height, muscleFactor });
    if (!onSave) {
      onClose();
    }
  };

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          mx: -2,
          mt: 4,
          mb: 2,
          py: 1.5,
          px: 2,
          backgroundColor: "#F4F7FE",
          borderBottom: "1px solid #E0E0E0",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" fontWeight={700} color="#111827">
          {t("patient-request.height-info.title")}
        </Typography>
      </Box>

      <Box
        sx={{
          px: { xs: 1, sm: 1.5 },
          pb: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <ByzMuiSaveButton onClick={handleSave} disabled={!height} />
          <ByzMuiCloseButton onClick={onClose} />
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.height-info.height")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <ByzMuiTextField
              fullWidth
              size="small"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              placeholder={t("patient-request.height-info.placeholder")}
              type="number"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" fontWeight={600}>
              Kas Faktörü
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <ByzMuiFormSelect
              fullWidth
              label="Kas Faktörü"
              value={muscleFactor}
              onChange={(e) => setMuscleFactor(String(e.target.value))}
              size="small"
            >
              <MenuItem value="">
                <em>Seçiniz</em>
              </MenuItem>
              <MenuItem value="1.0">1.0</MenuItem>
              <MenuItem value="1.2">1.2</MenuItem>
              <MenuItem value="1.4">1.4</MenuItem>
            </ByzMuiFormSelect>
          </Grid>
        </Grid>

        <Divider sx={{ mt: 3 }} />
      </Box>
    </OpenComponentDialog>
  );
};

export default HeightInfoModal;
